import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Box, Typography } from '@mui/material';
import logoImg from '../../assets/logo/Logo.webp';

interface Props {
  onComplete: () => void;
}

const AnimatedIntro: React.FC<Props> = ({ onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, 3000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.7, ease: 'easeInOut' } }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0d0d0d',
        overflow: 'hidden',
      }}
    >
      {/* Animated accent ring */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.12 }}
        transition={{ duration: 1.2, ease: 'easeOut' }}
        style={{
          position: 'absolute',
          width: 500,
          height: 500,
          borderRadius: '50%',
          border: '1px solid #c8a96e',
        }}
      />
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.06 }}
        transition={{ duration: 1.4, ease: 'easeOut', delay: 0.1 }}
        style={{
          position: 'absolute',
          width: 700,
          height: 700,
          borderRadius: '50%',
          border: '1px solid #c8a96e',
        }}
      />

      {/* Logo */}
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      >
        <Box
          sx={{
            bgcolor: 'transparent',
            borderRadius: 2,
            px: 3,
            py: 1.5,
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src={logoImg}
            alt="Sushigi Catering"
            sx={{ height: { xs: 72, md: 90 }, display: 'block' }}
          />
        </Box>
      </motion.div>

      {/* Tagline */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.7 }}
      >
        <Typography
          variant="overline"
          sx={{
            display: 'block',
            mt: 3,
            color: 'var(--color-accent)',
            letterSpacing: '0.35em',
            fontSize: '0.75rem',
            textAlign: 'center',
          }}
        >
          Catering de sushi
        </Typography>
      </motion.div>

      {/* Loading bar */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 2.6, ease: 'linear', delay: 0.3 }}
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 2,
          backgroundColor: '#c8a96e',
          transformOrigin: 'left center',
        }}
      />
    </motion.div>
  );
};

export default AnimatedIntro;
